import { Box, LinearProgress } from "@mui/material";
import { type FC, useCallback, useEffect, useMemo, useRef } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { config } from "../../config";
import type { UUID } from "../../utils/uuid";
import { EmailAddressSelect } from "../email-moment/components/email-address-selector";
import { useAccountLimitsContext } from "../context/account-limit-context";
import { useHandleMomentLexicalError } from "../generic/hooks/use-handle-moment-lexical-error";
import { useSetLexicalDesign } from "../generic/hooks/use-set-lexical-design";
import type { TagGroup } from "../merge-tag/components/types";
import { useFilterTags } from "../merge-tag/components/use-filter-tags";
import type { MergeTagV1, Tags } from "../merge-tag/types";
import { DefaultBodyPlugins } from "../message/editor/default-body-plugins";
import { DefaultContentEditable } from "../message/editor/default-content-editable";
import { useLexicalContext } from "../message/editor/lexical-context";
import { LexicalDynamicContentContextProvider } from "../message/editor/lexical-dynamic-content-context";
import { LexicalEditorWrapper } from "../message/editor/lexical-editor-wrapper";
import { DynamicContentPlugin } from "../message/editor/plugins/dynamic-content-plugin/dynamic-content-plugin";
import { GenericToolbar } from "../message/editor/plugins/generic-toolbar/generic-toolbar";
import { PollPluginMomentFormWrapper } from "../message/editor/plugins/poll-plugin/poll-plugin-moment-form-wrapper";
import { SurveyAnswersPlugin } from "../message/editor/plugins/survey-answers-plugin/survey-answers-plugin";
import { EDITOR_NODES } from "../message/editor/utils/nodes";
import { serialisedEditorDesignToPlainText } from "../message/editor/utils/serialised-editor-design-to-plain-text";
import { tableNodeReplacement } from "../message/editor/utils/use-styleable-columns";
import type { MomentFormMoment } from "../moment/types/moment-form-moment";
import { useMomentFormSurveys } from "../survey/hooks/use-moment-form-surveys";
import { useToTarget } from "../to/use-to-target";
import { CustomEmailHeaderSelect } from "./custom-email-header-select";
import { EmailSubjectField } from "./email-subject-field";
import { EmailTemplateLayout } from "./email-template-layout";
import { useConstructedDesign } from "./hooks/use-constructed-design";
import { type ToolbarSettings, useEmailToolbarSettings } from "./hooks/use-email-toolbar-settings";
import { useSelectedEmailDesign } from "./hooks/use-selected-email-design";

export type EmailMomentFormProps = {
  disabled?: boolean;
  showValidationErrors: boolean;
  mergeTags: Tags;
  channel: "work_email" | "personal_email";
  restrictedEmailFrom: UUID[];
};

export const LexicalEmailMomentForm: FC<EmailMomentFormProps> = ({
  disabled,
  showValidationErrors,
  mergeTags,
  channel,
  restrictedEmailFrom,
}) => {
  const { control, getValues } = useFormContext<MomentFormMoment>();
  const { enable_advanced_email_headers } = useAccountLimitsContext();

  const toTarget = useToTarget();

  const validationError = (
    fieldError: string | undefined,
    fieldTouched: boolean | undefined,
  ): string | undefined => {
    if (!fieldError) {
      return undefined;
    }

    return showValidationErrors || fieldTouched ? fieldError : undefined;
  };

  const filterTags = useFilterTags(
    mergeTags.filter((x) => x.version === 2),
    toTarget,
  );
  const getMessageBody = useCallback(
    () => serialisedEditorDesignToPlainText(getValues("design")),
    [getValues],
  );

  return (
    <>
      <Box maxWidth="md" pt={4}>
        <Controller
          name="from"
          control={control}
          render={({ field }) => (
            <EmailAddressSelect
              value={field.value || config.defaultEmailFrom}
              onChange={field.onChange}
              disabled={disabled}
              labelProps={{ sx: { m: 0 } }}
              restrictedEmailFrom={restrictedEmailFrom}
            />
          )}
        />
      </Box>
      <Box maxWidth="md" pt={2}>
        <Controller
          name="email_subject"
          control={control}
          render={({ field, fieldState }) => {
            const error = validationError(fieldState.error?.message, fieldState.isTouched);

            return (
              <EmailSubjectField
                value={field.value ?? ""}
                onChange={field.onChange}
                filterTags={filterTags}
                getMessageBody={getMessageBody}
                error={error}
                disabled={disabled}
              />
            );
          }}
        />
      </Box>
      {enable_advanced_email_headers && <CustomEmailHeaderSelect disabled={disabled} />}
      <Box pt={2} sx={{ width: "100%" }}>
        <LexicalEmailEditor
          disabled={disabled}
          showValidationErrors={showValidationErrors}
          filterTags={filterTags}
          v1Tags={mergeTags.filter((x) => x.version === 1)}
          channel={channel}
          restrictedEmailFrom={restrictedEmailFrom}
        />
      </Box>
    </>
  );
};

export const LexicalEmailEditor: FC<
  Omit<EmailMomentFormProps, "mergeTags"> & {
    filterTags: (x: string) => TagGroup;
    v1Tags: MergeTagV1[];
  }
> = ({ disabled, showValidationErrors, filterTags, v1Tags, channel }) => {
  const emailEditorScrollRef = useRef<HTMLDivElement>(null);
  const {
    control,
    formState: { touchedFields, errors, isDirty },
    getValues,
    setValue,
  } = useFormContext<MomentFormMoment>();

  useWatch({
    control,
    name: ["design", "email_design_id", "trigger_survey_id"],
  });
  const [design, email_design_id, triggerSurveyId] = getValues([
    "design",
    "email_design_id",
    "trigger_survey_id",
  ]);
  const { data: surveys } = useMomentFormSurveys();
  const isSurveyTrigger = !!triggerSurveyId;
  const triggerSurveyName = surveys?.find((s) => s.id === triggerSurveyId)?.title ?? null;

  const selectedEmailDesign = useSelectedEmailDesign(email_design_id);

  const {
    constructDesign: { data: constructedDesign, loading },
  } = useConstructedDesign(design, selectedEmailDesign);

  const setLexicalDesign = useSetLexicalDesign(design?.template);
  const toolbarSettings = useEmailToolbarSettings({
    selectedEmailDesign,
    templateSettings: design?.template,
    setEmailDesign: (x) => setValue("email_design_id", x?.id ?? null),
    isDirty,
  });

  const effectiveToolbarSettings = useMemo(() => {
    if (!toolbarSettings) return toolbarSettings;
    return {
      ...toolbarSettings,
      floatingElements: {
        ...toolbarSettings.floatingElements,
        text: [
          ...(toolbarSettings.floatingElements?.text ?? []),
          { type: "coPilot" as const, channel, format: "email" as const },
        ],
      },
    };
  }, [toolbarSettings, channel]);

  useEffect(() => {
    if (selectedEmailDesign) {
      if (!email_design_id) setValue("email_design_id", selectedEmailDesign.id);
    }
  }, [selectedEmailDesign, email_design_id, setValue]);

  const onError = useHandleMomentLexicalError();

  if (loading || !constructedDesign || !selectedEmailDesign || !effectiveToolbarSettings)
    return <LinearProgress />;

  return (
    <LexicalDynamicContentContextProvider>
      <LexicalEditorWrapper
        channelName={channel}
        keyID={email_design_id?.toString()}
        onError={(...args) => void onError(...args).catch(console.error)}
        disabled={disabled}
        error={showValidationErrors || touchedFields?.design ? errors.design?.message : undefined}
        isSurveyTrigger={isSurveyTrigger}
        triggerSurveyId={triggerSurveyId ?? null}
        triggerSurveyName={triggerSurveyName}
        renderLayout={(editorComponent) => (
          <EmailTemplateLayout
            settings={constructedDesign.settings}
            header={constructedDesign.header ?? undefined}
            headerSettings={constructedDesign.headerSettings ?? undefined}
            footer={constructedDesign.footer ?? undefined}
            editorComponent={editorComponent}
            templateSettings={design?.template}
            emailEditorScrollRef={emailEditorScrollRef}
          />
        )}
        filterTags={filterTags}
        format="email"
        design={constructedDesign?.design ?? undefined}
        nodes={tableNodeReplacement(EDITOR_NODES["email"])}
        slots={{
          contentEditable: ({ focusEditor, onRef }) => (
            <DefaultContentEditable
              focusEditor={focusEditor}
              onRef={onRef}
              channel={channel}
              disabled={disabled}
            />
          ),
          plugins: {
            head: () => (
              <EmailHeadPlugins toolbarSettings={effectiveToolbarSettings} disabled={disabled} />
            ),
            body: (historyState) => (
              <>
                <DefaultBodyPlugins
                  auto_focus={false}
                  channel={channel}
                  format="email"
                  disabled={disabled}
                  onChange={setLexicalDesign}
                  editorScrollRef={emailEditorScrollRef}
                  toolbarSettings={effectiveToolbarSettings}
                  styleableColumnConfig={constructedDesign.settings.layout}
                  historyState={historyState}
                  v1Tags={v1Tags}
                />
                <PollPluginMomentFormWrapper />
                <SurveyAnswersPlugin />
                <DynamicContentPlugin />
              </>
            ),
          },
        }}
      />
    </LexicalDynamicContentContextProvider>
  );
};

const EmailHeadPlugins: FC<{
  toolbarSettings: ToolbarSettings;
  disabled?: boolean;
}> = ({ toolbarSettings, disabled }) => {
  // This will eventually be rendered by the LexicalEditor calling the slot, so we can
  // use the context here
  const {
    editorModal: { handleOpen },
  } = useLexicalContext();

  return (
    <GenericToolbar
      channel={toolbarSettings.channel}
      topLevelElements={toolbarSettings.topLevelElements}
      disabled={!!disabled}
      openModal={handleOpen}
    />
  );
};
